'use client';
import React, { createContext, useCallback, useEffect, useRef, useState } from 'react';

import { EventTrackingController } from '../EventTrackingController';
import {
  SendEventOptions,
  SendEventParams,
  SetUserParams,
  EventTrackingContextType,
  EventTrackingProviderProps,
  DatadogEventData,
  KakaoPixelMethods,
  KakaoPixelEventParams,
  MetaPixelEventNames,
  MetaPixelParams
} from '../types';

import { GoogleTrackingScript, KakaoPixelScript, MetaPixelScript, TiktokPixelScript } from './EventTrackingScripts';

export const EventTrackingContext = createContext<EventTrackingContextType>({
  eventUser: undefined,
  setUser: () => {},
  sendEvent: () => {},
  sendMetaPixelEvent: () => {},
  sendKakaoPixelEvent: () => {},
  sendDatadogEvent: () => {},
  unsetUser: () => {},
  addSentEventHistory: () => false,
  pageName: '',
  setPageName: () => {},
  properties: {
    mixpanelDeviceId: ''
  }
});

export const EventTrackingProvider = ({
  children,
  appId,
  clientId,
  landingId,
  isUseAuthPackages = true,
  mixpanelProps,
  airbridgeProps,
  metaPixelProps,
  tiktokPixelProps,
  kakaoPixelProps,
  navigationEvent,
  googleTrackingProps,
  datadogProps
}: EventTrackingProviderProps) => {
  const isUserUnset = useRef(true);
  const [eventUser, setEventUser] = useState<EventTrackingContextType['eventUser']>(isUseAuthPackages ? undefined : null);
  const [pageName, setPageName] = useState<string>('');
  const sentEventHistory = useRef<string[]>([]);
  const clearSentViewEventTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [eventController] = useState<EventTrackingController>(() => {
    return new EventTrackingController({
      appId,
      clientId,
      landingId,
      ...mixpanelProps,
      ...airbridgeProps,
      ...metaPixelProps,
      ...tiktokPixelProps,
      ...googleTrackingProps,
      ...datadogProps,
      ...kakaoPixelProps
    });
  });

  const setUser = (params: SetUserParams) => {
    setEventUser(params);
    eventController.setUser(params);
    isUserUnset.current = false;
  };

  const unsetUser = () => {
    const searchParams = new URLSearchParams(window.location.search);
    if ((typeof eventUser === 'undefined' && !(searchParams.has('code') && searchParams.has('grant_type'))) || eventUser) {
      setEventUser(null);
      if (!isUserUnset.current) {
        eventController.unsetUser();
        isUserUnset.current = true;
      }
    }
  };

  const sendEvent = (params: SendEventParams, options?: SendEventOptions) => {
    eventController.sendEvent(params, options);
  };

  const sendMetaPixelEvent = (eventName: MetaPixelEventNames, eventParams?: MetaPixelParams) => {
    eventController.sendMetaPixelEvent(eventName, eventParams);
  };

  const sendKakaoPixelEvent = (methodName: KakaoPixelMethods, eventParams?: KakaoPixelEventParams) => {
    eventController.sendKakaoPixelEvent(methodName, eventParams);
  };

  const sendDatadogEvent = (data: DatadogEventData) => {
    eventController.sendDatadogEvent(data);
  };

  const addSentEventHistory = useCallback((data: Omit<SendEventParams, 'action'>, isAllowRetry: boolean = false): boolean => {
    const jsonData = JSON.stringify(data);
    const isNewEvent = !sentEventHistory.current.includes(jsonData);

    if (isNewEvent) {
      sentEventHistory.current.push(jsonData);
      if (isAllowRetry) {
        setTimeout(() => {
          const index = sentEventHistory.current.findIndex((eventString: string) => eventString === jsonData);

          if (index > -1) {
            sentEventHistory.current.splice(index, 1);
          }
        }, 2000);
      }
    }

    return isNewEvent;
  }, []);

  const clearSentEventHistory = useCallback(() => {
    if (clearSentViewEventTimer.current) {
      clearTimeout(clearSentViewEventTimer.current);
    }
    clearSentViewEventTimer.current = setTimeout(() => {
      if (!pageName) return;
      sentEventHistory.current = sentEventHistory.current.filter((eventString: string) => {
        try {
          const savedEvent = JSON.parse(eventString);
          return savedEvent.category === pageName || savedEvent.attributes.page === pageName;
        } catch {
          return false;
        }
      });
      clearSentViewEventTimer.current = null;
    }, 2000);
  }, [pageName]);

  useEffect(() => {
    navigationEvent?.registerNavigationEvent(clearSentEventHistory);
    return () => {
      navigationEvent?.removeNavigationEvent(clearSentEventHistory);
    };
  }, [navigationEvent, clearSentEventHistory]);

  useEffect(() => {
    // NOTE: PageNavigationEventContext에서 가져온 navigationEvent가 없을 경우에 수행하는 대체액션입니다.(일부 액션이 비정상적으로 동작할 수 있습니다.)
    if (navigationEvent) return;

    clearSentEventHistory();
  }, [clearSentEventHistory, navigationEvent]);

  return (
    <>
      {metaPixelProps?.metaPixelId && <MetaPixelScript />}
      {tiktokPixelProps?.tiktokPixelId && <TiktokPixelScript />}
      {googleTrackingProps && <GoogleTrackingScript {...googleTrackingProps} />}
      {kakaoPixelProps && <KakaoPixelScript />}
      <EventTrackingContext.Provider
        value={{
          eventUser,
          setUser,
          sendEvent,
          sendMetaPixelEvent,
          sendKakaoPixelEvent,
          sendDatadogEvent,
          unsetUser,
          addSentEventHistory,
          pageName: pageName,
          setPageName,
          properties: {
            mixpanelDeviceId: eventController.getMixpanelDeviceId()
          }
        }}
      >
        {children}
      </EventTrackingContext.Provider>
    </>
  );
};
